import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { PlayerProfile, SlotSpinFn, Theme } from '@goodtime/engine';
import {
  BOT_ARCHETYPE_LIST,
  type Biome,
  type BotArchetype,
  type SpinSlotResponse,
  type WheelObservableState,
  type WheelPhase,
  type WheelSpinSettlement,
} from '@goodtime/shared';
import { submitWheelCommand } from '../../api/wheelCommand';
import { useWheelRoomStream } from '../../hooks/useWheelRoomStream';
import { BalancePill } from '../BalancePill';
import { WheelPayoutView } from './WheelPayoutView';
import { WheelReelRaceView } from './WheelReelRaceView';
import { WheelResolutionView } from './WheelResolutionView';
import { WheelResolveView } from './WheelResolveView';
import { GestureToast } from './GestureToast';
import { rememberSentGesture } from './gestureMemory';
import { SeatJoinToast } from './SeatJoinToast';
import { ProfileCard } from './ProfileCard';
import { useCastFavourites } from '../../hooks/useCastFavourites';
// Audit backlog D3 (2026-05-28) — mobile dev panel. Mounts when the
// URL search string contains `?dev=panel`. Renders state at a glance
// for debug sessions; inert in production.
import { WheelDevPanel, isDevPanelEnabled } from './WheelDevPanel';
// WheelLoadoutView (the MK-style item-roll loadout screen) is retired —
// the producer redesign drops the setup-step entirely so the flow stays
// in-game. The file remains on disk while the new flow beds in.
import { CornerSpark, PearlStud, Sparkle } from '../dialog/DialogOrnaments';
import bonusCoinUrl from '../../assets/reel/forest/bonus.png';
// Wrapped gift icon — the universal "special items" glyph across the
// REEL_RACE chrome: it lands on the reel as the {@code item_drop}
// symbol, decorates the WHEEL_REVEAL "Setting the Table" card next
// to the coin, and counts items in the BalancePill bonus cluster.
// Previously a star (apps/mobile/src/assets/icon/star.png); swapped
// because the star aliased both the literal {@code LUCKY_STAR} item
// and the in-cell placeholder, making the cycle reveal read as
// "star landed → cycled → another star" for that one item. Gift box
// has no equivalent collision in the catalog.
import itemDropGiftUrl from '../../assets/reel/forest/item_drop.png';

/** First-entry loading-bar minimum visible duration. The bar starts
 *  on roomId set and fills 0→100% over this window; it disappears
 *  the first frame both (a) at least this many ms have elapsed AND
 *  (b) the first SSE state has landed. Tuned so a fast localhost
 *  server doesn't make the bar flash and gone, while a slow staging
 *  server still hides the bar as soon as the room is ready. */
const FIRST_ENTRY_FLOOR_MS = 1500;

/** Per-biome eyebrow shown above the headline on the loader story
 *  card. Adds biome flavour without shouldering the whole headline —
 *  the universal "Collect the gold coins for the next round." copy
 *  is the load-bearing line. */
const LOADING_EYEBROW_BY_BIOME: Record<Biome['id'], string> = {
  kingdom: 'Setting the table',
  cove: 'Dropping anchor',
  frost: 'Lighting the lamps',
  frontier: 'Saddling up',
  neon: 'Powering on',
  egypt: 'Unsealing the tomb',
  // <scaffold:loading-eyebrow> — scaffoldBiome appends a new biome's loader eyebrow above this line.
  candy: 'Stoking the fizz',
  // NB: this map is an exhaustive Record<Biome['id']> — a new biome MUST have an entry or the build fails.
};

/**
 * T12 — biome-panorama backdrop lookup. Mirrors the pattern in
 * {@link MapLayerBackground}: a Vite glob picks up every PNG in
 * {@code src/assets/map_bg/} and exposes a {biomeId → url} map at
 * runtime. Missing biomes fall back to the navy gradient on the
 * outer wrapper (some biomes' map_bg may not yet be commissioned).
 */
const BACKDROP_MODULES = import.meta.glob<{ default: string }>(
  '../../assets/map_bg/*.png',
  { eager: true, query: '?url' },
);
const BACKDROP_BY_BIOME: Partial<Record<Biome['id'], string>> = Object.fromEntries(
  Object.entries(BACKDROP_MODULES)
    .map(([path, mod]) => {
      const id = path.match(/([^/]+)\.png$/)?.[1] ?? null;
      return id ? [id, mod.default] : null;
    })
    .filter((entry): entry is [string, string] => entry !== null),
);

/**
 * Theme id → biome id. Slot themes use product-level names
 * ('forest', 'underwater'); world-map biomes use storyworld names
 * ('kingdom', 'cove'). Frost / frontier / neon are aligned. Falls
 * back to 'kingdom' so an unknown theme still gets the Greendale
 * panorama as a sensible default rather than flat navy.
 */
function biomeIdForTheme(theme: Theme | null | undefined): Biome['id'] {
  switch (theme?.id) {
    case 'forest':
      return 'kingdom';
    case 'underwater':
      return 'cove';
    case 'frost':
      return 'frost';
    case 'frontier':
      return 'frontier';
    case 'neon':
      return 'neon';
    default:
      return 'kingdom';
  }
}





export interface WheelLobbyViewProps {
  /** Wheel-room UUID. When set, the view streams live seats and
   * phase from the server. When null/undefined the view shows the
   * U1 design-review scaffold (mock seats + the 8-archetype gallery). */
  roomId?: string | null;
  /** Local player UUID. When both roomId and playerId are set, the
   * view auto-issues a JOIN_ROOM command on first render so the
   * player gets a seat in the live room (replacing the first bot).
   * Spin buttons in CHARGING/REEL_RACE call POST commands as this
   * playerId. Null = read-only audience mode. */
  playerId?: string | null;
  /** Social — the local player's display name, threaded into the
   * JOIN_ROOM args so OTHER clients can render a "{name} took {bot}'s
   * seat" toast when this player joins. Null/empty falls back to
   * "Someone" on the receiving client. */
  playerName?: string | null;
  /** Surface a "couldn't create the room" hint above the cast. The
   * view still renders in mock mode so the user isn't stuck on a
   * blank screen. */
  errorMessage?: string | null;
  /** Display name of the wheel room — typically the world-map node
   * name like "Forest Wheel · Whispering Woods". The view splits at
   * the " · " separator: primary line in gold caps, subtitle below.
   * Defaults to the design-review demo name when unset. */
  roomName?: string | null;
  /** Social MVP — the room's shareable invite code (e.g. "GTC-4F2A").
   *  Threaded to {@link WheelReelRaceView}'s InviteAvatarButton so the
   *  host can copy + share it. Null on rooms without a code. */
  inviteCode?: string | null;
  /** Pixi theme used for the embedded slot scene during REEL_RACE.
   * Should match the room's theme id (e.g. forest). Pass-through to
   * {@link WheelReelRaceView}. Already reflects the player's reel-skin
   * override when one is chosen — the host resolves that upstream. */
  theme?: Theme | null;
  /** Reel-skin options (2026-06-06) — the player's unlocked slot
   * themes, offered in the Reel Race's top-left skin selector.
   * Pass-through to {@link WheelReelRaceView}. */
  availableSkins?: readonly { id: string; name: string }[];
  /** Currently-displayed skin/theme id (for the selector's checkmark). */
  selectedSkinId?: string;
  /** Fires with the chosen theme id when the player switches skin. */
  onSelectSkin?: (themeId: string) => void;
  /** Player wallet/profile, threaded into the slot scene's HUD. */
  profile?: PlayerProfile | null;
  /** F1 — explicit audience-mode flag. Equivalent to
   * {@code playerId == null}, but threaded as a separate prop so the
   * lobby can render audience-only chrome (a "Take a seat" CTA, an
   * audience-mode overlay watermark) even when the local player has
   * a UUID — they're just choosing to spectate this match. */
  audience?: boolean;
  /** F1 — promote audience → player. Fires when the spectator taps
   * "Take a seat ▶". Host flips its own playerId-binding to non-null
   * on the next render and {@link WheelLobbyView}'s auto-JOIN effect
   * sends JOIN_ROOM. No-op if {@link audience} is false. */
  onTakeSeat?: () => void;
  /** F2.2 — onboarding curriculum step (1..5) of the local player.
   *  Threads down to phase views that gate UI per UX §4 (e.g. the
   *  Stake screen hides Shield + Lance until session 3). Null /
   *  undefined disables curriculum gating — every UI is unlocked,
   *  matching the post-cohort default. */
  onboardingSession?: number | null;
  /** Tap-target for the gear icon on the embedded slot HUD during
   *  REEL_RACE. The wheel room's CanvasHost otherwise has no
   *  onOpenSettings wired and the gear becomes a no-op. */
  onOpenSettings?: (() => void) | null;
  /** Wallet coin balance — threaded into the top-right BalancePill.
   *  App-level state subscribed to {@code profile.events}. */
  coinBalance?: number;
  /** Wallet gem balance — clickable in the pill to open the shop. */
  gemBalance?: number;
  /** Login streak chip for the pill. */
  streak?: number;
  /** Player level chip for the pill. */
  level?: number;
  /** Open the gem shop. Null/undefined hides the click affordance
   *  but keeps the chip visible as a status read. */
  onTapGems?: (() => void) | null;
  /** T1 polish (2026-05-29) — fires when the room transitions into
   *  PAYOUT, i.e. the match just ended. Host uses this to reconcile
   *  the server wallet (gem credits from reels / wheel / podium are
   *  awarded server-side and need a sync to land on the local
   *  profile + fire the {@link GemDropToast}). Receives the match
   *  number that just ended so multi-fire suppression is trivial. */
  onPayoutLanded?: ((matchNumber: number) => void) | null;
}

/**
 * U1 + U3 deliverable: cast strip + bot badge + profile card +
 * (optional) live SSE room stream.
 *
 * <p>Mock mode (no roomId): shows a sample 6-seat room ("Forest
 * Wheel · Whispering Woods") plus the 8-archetype gallery. Tapping
 * any avatar opens the {@code ProfileCard} modal. Used for design
 * review and as the screen behind the 🎭 Cast pill in the dev nav.
 *
 * <p>Live mode (roomId set): subscribes to
 * {@code GET /api/rooms/{roomId}/stream?role=audience}, drives the
 * cast strip from {@code WheelObservableState.seats}, and replaces
 * the static "Match in 0:24" header with the current phase pill.
 *
 * <p>Favourites persist server-side via the cast-favourites endpoints
 * (see the U11 note in the component body).
 */
export function WheelLobbyView({
  roomId = null,
  playerId = null,
  playerName = null,
  errorMessage = null,
  theme = null,
  availableSkins,
  selectedSkinId,
  onSelectSkin,
  profile = null,
  audience = false,
  onTakeSeat,
  onboardingSession = null,
  onOpenSettings = null,
  coinBalance = 0,
  gemBalance = 0,
  streak = 0,
  level = 1,
  onTapGems = null,
  onPayoutLanded = null,
  inviteCode = null,
}: WheelLobbyViewProps = {}) {
  // Live SSE wiring — no-op when roomId is null. Subscribe as
  // PLAYER (not the default AUDIENCE) when the local user owns a
  // seat in this room. The role drives the heartbeat loop inside
  // {@link useWheelRoomStream}: PLAYER pings every 10s so the
  // server's idle-sweeper doesn't synthesize a LEAVE_ROOM after
  // 30s of silence — without this, the player gets evicted mid-
  // match and the room reverts to bots-only, leaving the client
  // stuck on the loader because bots refuse to act in a
  // humanless room (defense-in-depth in WheelBotPolicy).
  const streamRole = audience || !playerId ? 'AUDIENCE' : 'PLAYER';
  const {
    state: liveState,
    reactions,
    seatToasts,
    status: streamStatus,
  } = useWheelRoomStream(
    roomId,
    streamRole,
    playerId ?? undefined,
  );

  // U11 (2026-05-30) — cast favourites state. Tapping a seat avatar
  // in any phase view sets {@link selectedArchetype}; the matching
  // {@link ProfileCard} mounts below the lobby chrome. Hook hydrates
  // from the server's cast_favourites table on first mount; toggles
  // round-trip through the {@code POST .../cast-favourites/{archetype}}
  // endpoint. Idle when audience-mode (playerId === null) — the hook
  // accepts null and falls back to an in-memory-only toggle.
  const [selectedArchetype, setSelectedArchetype] =
    useState<BotArchetype | null>(null);
  const cast = useCastFavourites(playerId ?? null);
  // Preload all 8 fan counts on mount so the ProfileCard chip is
  // populated before the first avatar tap. Without this the chip
  // would flash 0 → real value as the lazy ensureFanCount runs.
  useEffect(() => {
    if (!playerId) return;
    for (const meta of BOT_ARCHETYPE_LIST) {
      void cast.ensureFanCount(meta.id);
    }
    // {@code cast} is stable per playerId from the hook, but listing
    // it as a dep keeps the lint rule happy.
  }, [playerId, cast]);

  // Bump on each new prep cycle so the loader's CSS animation
  // restarts. Triggers when phase transitions INTO IDLE — covers
  // first entry (no prior phase) and the post-PAYOUT → IDLE handoff
  // between matches. Ignores transitions WITHIN the prep window
  // (IDLE → CHARGING) since those should keep filling, not restart.
  const [prepCycleKey, setPrepCycleKey] = useState(0);
  // True while the Reel Race slot's info modal (paytable / items) is
  // open — mirrored up from WheelReelRaceView so we can hide the
  // floating BalancePill, which otherwise overlaps the modal's title.
  const [reelRaceInfoOpen, setReelRaceInfoOpen] = useState(false);
  const lastPhaseRef = useRef<WheelPhase | null>(null);
  useEffect(() => {
    const cur = liveState?.phase ?? null;
    const prev = lastPhaseRef.current;
    if (cur === 'IDLE' && prev !== 'IDLE' && prev !== 'CHARGING') {
      setPrepCycleKey((k) => k + 1);
    }
    lastPhaseRef.current = cur;
  }, [liveState?.phase]);

  // T1 polish — fire onPayoutLanded exactly once per PAYOUT transition.
  // Gem credits from the match (reels / wheel / podium drops) are
  // server-side; the host uses this callback to reconcile the wallet
  // so the {@link GemDropToast} can fire. Suppression keys on
  // matchNumber so a transient re-render of the same PAYOUT doesn't
  // double-fire.
  const lastPayoutMatchRef = useRef<number | null>(null);
  useEffect(() => {
    if (!onPayoutLanded) return;
    if (liveState?.phase !== 'PAYOUT') return;
    const match = liveState.matchNumber ?? 0;
    if (match === lastPayoutMatchRef.current) return;
    lastPayoutMatchRef.current = match;
    onPayoutLanded(match);
  }, [liveState?.phase, liveState?.matchNumber, onPayoutLanded]);

  // Auto-join: when both roomId and playerId are set, fire a
  // JOIN_ROOM once. Tracked per-room so re-entering the same room
  // (e.g. SSE reconnect that re-mounts the hook) doesn't double-join.
  // The server's handleJoinRoom is idempotent for the same playerId
  // anyway — replays land on the existing PLAYER seat — but skipping
  // the redundant POST keeps logs clean.
  //
  // T-F1 (2026-05-31) — gate on playerName too. Without this, the
  // auto-JOIN can fire BEFORE {@code useServerConnection} resolves
  // the player's displayName, sending an empty string the server
  // resolves to the "Player <uuid-4>" placeholder. By waiting for a
  // non-blank name we avoid burning the per-room idempotency token
  // on a join that can't carry the real name. The 30s heartbeat-grace
  // window means a delayed first-join here is invisible to the player.
  const joinedRoomsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!roomId || !playerId) return;
    if (!playerName || playerName.trim().length === 0) return;
    if (joinedRoomsRef.current.has(roomId)) return;
    joinedRoomsRef.current.add(roomId);
    void submitWheelCommand(roomId, 'JOIN_ROOM', playerId, {
      displayName: playerName,
    }).catch((e) => {
      console.warn('[WheelLobbyView] JOIN_ROOM failed', e);
    });
  }, [roomId, playerId, playerName]);

  const playerSeatId = useMemo<number | null>(() => {
    if (!liveState || !playerId) return null;
    const seat = liveState.seats.find(
      (s) => s.kind === 'PLAYER' && s.playerId === playerId,
    );
    return seat ? seat.seatId : null;
  }, [liveState, playerId]);

  // Auto-rejoin: if the heartbeat sweeper reclaimed our seat while
  // the app was inactive, the room is now paused-IDLE with all bots
  // and won't kick off again on its own. Re-fire JOIN_ROOM the moment
  // we observe that we previously held a seat but no longer do — this
  // unsticks a player returning from a long background to the
  // "Setting the table" loader. {@code hadSeatRef} gates on prior
  // ownership so this doesn't race the initial JOIN_ROOM (which has
  // a brief window where liveState has landed but the seat swap
  // hasn't been processed yet).
  const hadSeatRef = useRef(false);
  const rejoinInFlightRef = useRef(false);
  useEffect(() => {
    if (playerSeatId !== null) {
      hadSeatRef.current = true;
      rejoinInFlightRef.current = false;
    }
  }, [playerSeatId]);
  useEffect(() => {
    if (!roomId || !playerId) return;
    if (!liveState) return;
    if (playerSeatId !== null) return;
    if (!hadSeatRef.current) return;
    if (rejoinInFlightRef.current) return;
    // T-F1 (2026-05-31) — same playerName gate as the initial join.
    // If the rejoin trigger fires before useServerConnection has the
    // name, defer until it lands; the rejoin will re-trigger when
    // playerName changes.
    if (!playerName || playerName.trim().length === 0) return;
    rejoinInFlightRef.current = true;
    void submitWheelCommand(roomId, 'JOIN_ROOM', playerId, {
      displayName: playerName,
    }).catch((e) => {
      rejoinInFlightRef.current = false;
      console.warn('[WheelLobbyView] auto-rejoin failed', e);
    });
  }, [roomId, playerId, liveState, playerSeatId, playerName]);

  // ---- slot spinFn bridge (M1.5j) -------------------------------------
  // The slot scene calls spinFn(bet); we POST REEL_RACE_SPIN, then wait
  // for the next SSE delta carrying a fresh seat update for the local
  // player. When it lands, we synthesize a SpinSlotResponse and resolve
  // the promise so the slot scene animates the server-driven reels
  // (with bonus cells rendering as the bonus symbol). One pending
  // resolver at a time — the slot scene gates concurrent spins via its
  // own `spinning` flag.
  //
  // Resolution discriminator: we capture a snapshot of the player's
  // seat (tokens / grid / bonusCells) at spin-start and compare against
  // it on every subsequent state delta. ANY change resolves the
  // promise. Capturing-then-comparing rather than dedupe-by-grid
  // matters for two reasons:
  //   1. Race safety: the resolver is registered BEFORE the POST so an
  //      SSE delta racing ahead of the 202 response (common on fast
  //      LAN) still has somewhere to deliver. If we used a previous-
  //      grid ref instead, the watcher would have to read it from
  //      whenever-it-was-last-updated, which doesn't survive the race.
  //   2. Same-grid spins: the JSON-patch differ in SseRoomBroadcaster
  //      doesn't emit a replace op for a deeply-equal value, so a
  //      genuinely-identical grid roll would never trigger the watcher
  //      under the old grid-equality dedupe. The seat snapshot still
  //      detects the spin via tokens/bonusCells changes.
  type PendingSpin = {
    resolve: (r: SpinSlotResponse) => void;
    reject: (err: unknown) => void;
    bet: number;
    /** Seat fields at spin-start; null if no live state yet (the slot
     * scene gates spins on roomId/playerId, but the seat lookup can
     * still miss during connection). */
    startSeat: {
      tokens: number;
      grid: readonly string[];
      bonusCells: readonly number[];
    } | null;
  };
  const pendingSpinRef = useRef<PendingSpin | null>(null);

  // Refs so slotSpinFn (memoised on roomId/playerId) reads the latest
  // live state without re-creating itself on every SSE tick — the
  // callback identity stays stable so CanvasHost doesn't tear down
  // the slot scene mid-session.
  const liveStateRef = useRef<WheelObservableState | null>(null);
  const playerSeatIdRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    liveStateRef.current = liveState;
  }, [liveState]);
  useLayoutEffect(() => {
    playerSeatIdRef.current = playerSeatId;
  }, [playerSeatId]);

  const slotSpinFn = useCallback<SlotSpinFn>(
    async (bet: number) => {
      if (!roomId || !playerId) {
        throw new Error('slot spin requires room + player');
      }
      // Guard against concurrent submissions — slot scene shouldn't,
      // but defensive belt-and-braces.
      if (pendingSpinRef.current) {
        pendingSpinRef.current.reject(new Error('superseded by next spin'));
        pendingSpinRef.current = null;
      }

      // Snapshot the seat now so the watcher's discriminator survives
      // the rare same-grid roll (token/bonusCell deltas still trigger).
      const stateNow = liveStateRef.current;
      const seatIdNow = playerSeatIdRef.current;
      let startSeat: PendingSpin['startSeat'] = null;
      if (stateNow && seatIdNow !== null) {
        const seat = stateNow.seats.find((s) => s.seatId === seatIdNow);
        if (seat) {
          startSeat = {
            tokens: seat.tokens,
            grid: seat.lastSpinGrid,
            bonusCells: seat.lastSpinBonusCells,
          };
        }
      }

      // Build the entry + register it BEFORE the POST. If the SSE
      // delta arrives before the 202 response (the original race),
      // the watcher already has a pending entry to deliver into.
      let entry!: PendingSpin;
      let timeoutId = 0;
      const promise = new Promise<SpinSlotResponse>((resolve, reject) => {
        timeoutId = window.setTimeout(() => {
          if (pendingSpinRef.current === entry) {
            pendingSpinRef.current = null;
            reject(new Error('spin timeout'));
          }
        }, 5_000);
        entry = {
          resolve: (r) => {
            window.clearTimeout(timeoutId);
            resolve(r);
          },
          reject: (err) => {
            window.clearTimeout(timeoutId);
            reject(err);
          },
          bet,
          startSeat,
        };
        pendingSpinRef.current = entry;
      });

      try {
        await submitWheelCommand(roomId, 'REEL_RACE_SPIN', playerId);
      } catch (e) {
        // POST failed (wrong phase, room busy, network) — drop the
        // pending entry, kill the timer, and surface to the slot
        // scene so its spinDriven catch path runs forceLandAll(). The
        // internal `promise` is left pending (no rejection) so we
        // don't trip an unhandled-rejection warning by orphaning it.
        if (pendingSpinRef.current === entry) {
          pendingSpinRef.current = null;
        }
        window.clearTimeout(timeoutId);
        throw e;
      }

      return promise;
    },
    [roomId, playerId],
  );

  // Watch for seat updates and resolve the pending spin promise. Seat
  // unchanged = nothing to do; any change in tokens/grid/bonusCells
  // since spin-start = the server processed our spin.
  useEffect(() => {
    if (!liveState || playerSeatId === null) return;
    const pending = pendingSpinRef.current;
    if (!pending) return;
    const selfSeat = liveState.seats.find((s) => s.seatId === playerSeatId);
    if (!selfSeat) return;
    const grid = selfSeat.lastSpinGrid;
    if (!grid || grid.length === 0) return;

    const start = pending.startSeat;
    const seatUnchanged =
      start !== null &&
      start.tokens === selfSeat.tokens &&
      start.grid.length === grid.length &&
      start.grid.every((s, i) => s === grid[i]) &&
      start.bonusCells.length === selfSeat.lastSpinBonusCells.length &&
      start.bonusCells.every((c, i) => c === selfSeat.lastSpinBonusCells[i]);
    if (seatUnchanged) return;

    pendingSpinRef.current = null;
    pending.resolve(
      buildSpinResponse(
        grid,
        selfSeat.tokens,
        pending.bet,
        profile?.balance ?? 0,
        selfSeat.lastSpinSettlement,
      ),
    );
  }, [liveState, playerSeatId, profile]);
  // ---------------------------------------------------------------------

  // Phase dispatch: when live, swap in the per-phase view. IDLE and
  // CHARGING fall through to the loader; everything else routes to
  // its own phase view.
  const phaseContent = (() => {
    if (!liveState) return null;
    if (liveState.phase === 'REEL_RACE') {
      return (
        <WheelReelRaceView
          state={liveState}
          selfSeatId={playerSeatId}
          theme={theme}
          availableSkins={availableSkins}
          selectedSkinId={selectedSkinId}
          onSelectSkin={onSelectSkin}
          onInfoModalOpenChange={setReelRaceInfoOpen}
          profile={profile}
          onSpin={null}
          roomId={roomId}
          playerId={playerId}
          reactions={reactions}
          inviteCode={inviteCode}
          // U11 — tap a bot seat avatar in the right rail to open the
          // ProfileCard. Mirrors the prop threaded to WheelPayoutView.
          onArchetypeTap={(archetype) => setSelectedArchetype(archetype)}
          // M1.5j: server is fully authoritative for the reel grid. The
          // slotSpinFn submits REEL_RACE_SPIN AND awaits the SSE delta
          // with the new grid; the slot scene lands those exact symbols
          // (bonus cells become the bonus icon — visual matches the
          // multiplayer math). No separate onSlotSpinFired hook needed
          // since the spinFn does the submit itself.
          slotSpinFn={
            playerSeatId !== null && playerId !== null && roomId !== null
              ? slotSpinFn
              : null
          }
          onboardingSession={onboardingSession}
          onOpenSettings={onOpenSettings}
        />
      );
    }
    if (
      liveState.phase === 'STAKE' ||
      liveState.phase === 'WHEEL_REVEAL' ||
      liveState.phase === 'WHEEL_RESOLVE'
    ) {
      // STAKE + WHEEL_RESOLVE share one screen — the wheel stays put
      // and the chrome shifts. STAKE shows a clock-fill timer, the
      // seat avatars are tappable (SELECT_SEAT swap) and the player's
      // loadout strip is visible. The disc starts spinning when
      // WHEEL_RESOLVE lands. WHEEL_REVEAL is retired; a snapshot
      // landing in it falls through to the spin view too.
      return (
        <WheelResolveView
          state={liveState}
          selfSeatId={playerSeatId}
          theme={theme}
          playerId={playerId}
          roomId={roomId}
          // U11 — phase-gated rim tap. STAKE keeps SELECT_SEAT swap;
          // WHEEL_RESOLVE / WHEEL_REVEAL open the ProfileCard for the
          // tapped archetype. Same callback as the other two phase
          // views so the lobby owns one selectedArchetype state.
          onArchetypeTap={(archetype) => setSelectedArchetype(archetype)}
        />
      );
    }
    if (liveState.phase === 'WHEEL') {
      // Legacy three-sub-spin path — preserved for backward compat
      // until the WHEEL phase enum is fully retired.
      return (
        <WheelResolutionView
          state={liveState}
          selfSeatId={playerSeatId}
          onboardingSession={onboardingSession}
        />
      );
    }
    if (liveState.phase === 'PAYOUT') {
      return (
        <WheelPayoutView
          state={liveState}
          selfSeatId={playerSeatId}
          theme={theme}
          reactions={reactions}
          onArchetypeTap={(archetype) => setSelectedArchetype(archetype)}
        />
      );
    }
    return null;
  })();

  // F1 — overlay the audience-mode chrome on top of every phase view
  // (and the pre-match lobby below). Renders nothing in player mode
  // so existing layouts are untouched.
  //
  // F1.next — watch-first gate (UX §4.1). On session 1 the player's
  // first wheel-room entry is forced into audience mode for ≥30s
  // before the take-seat affordance unlocks. Tracked via a timestamp
  // captured at the moment audience mode starts; the AudienceModeCta
  // computes its own ticking countdown.
  const watchFirstStartMsRef = useRef<number | null>(null);
  useEffect(() => {
    if (audience && onboardingSession === 1) {
      if (watchFirstStartMsRef.current === null) {
        watchFirstStartMsRef.current = Date.now();
      }
    } else {
      watchFirstStartMsRef.current = null;
    }
  }, [audience, onboardingSession]);
  const watchFirstUntilMs =
    audience && onboardingSession === 1 && watchFirstStartMsRef.current !== null
      ? watchFirstStartMsRef.current + 30_000
      : null;

  const audienceChrome = audience ? (
    <AudienceModeCta
      onTakeSeat={onTakeSeat ?? null}
      seatsAvailable={
        liveState ? liveState.seats.some((s) => s.kind === 'BOT' || !s.online) : true
      }
      watchFirstUntilMs={watchFirstUntilMs}
    />
  ) : null;

  // F2.2.next — per-session tutorial overlays (UX §4 microcopy).
  // Each one fires exactly once per profile per session step;
  // dismissal persists in localStorage so the tour never re-appears.
  // Session-2 fires on first STAKE phase entry; session-4 fires the
  // first time the server advances the onboarding pointer to 4 (after
  // the player crosses the 50-token jackpot-eligibility threshold).
  const tutorialChrome = (() => {
    if (roomId === null) return null;
    if (onboardingSession === 2 && liveState?.phase === 'STAKE') {
      return <Session2StakeOverlay />;
    }
    if (onboardingSession === 4) return <Session4JackpotUnlockedOverlay />;
    return null;
  })();

  // Bonus side of the BalancePill — pot + collected items, derived
  // from the local seat in the SSE state. Folded into the wallet pill
  // per producer brief: pot count (coin glyph + N) and a SINGLE item
  // chip (biome bonus glyph + total count). Deploy moves to the
  // wheel's STAKE surface — pill chips are status-only. Absent in
  // audience mode (no seat) or before the first SSE delta lands.
  const selfSeat =
    liveState && playerSeatId !== null
      ? liveState.seats.find((s) => s.seatId === playerSeatId) ?? null
      : null;
  const bonusProps = selfSeat
    ? {
        potCount: selfSeat.tokens,
        potIconUrl: bonusCoinUrl,
        itemCount: selfSeat.loadout.length,
        itemIconUrl: itemDropGiftUrl,
      }
    : null;
  // Match-wrap screen (PAYOUT phase) has its OWN coin chrome
  // (wallet summary in the wrap card), so the floating BalancePill
  // is redundant + visually competes with it. Producer brief
  // 2026-05-12: "hide the coin hub on the match wrap screen."
  const hidePillOnMatchWrap = liveState?.phase === 'PAYOUT';
  // Hide the floating coin HUD while the Reel Race info modal (paytable /
  // items catalog) is open — it's a full-screen sheet and the pill would
  // otherwise overlap its title. Producer brief 2026-06-10. The phase
  // guard stops a stale-open flag from suppressing the pill once we've
  // left Reel Race.
  const hidePillForInfoModal =
    reelRaceInfoOpen && liveState?.phase === 'REEL_RACE';
  const walletChrome = hidePillOnMatchWrap || hidePillForInfoModal ? null : (
    <div
      style={{
        position: 'absolute',
        // iOS pre-flight (2026-06-07) — drop below the Dynamic Island /
        // status bar so the coin HUD isn't occluded. env() is 0 on web,
        // ~59px on iPhone Pro models. Matches MapHud / BackToMapButton.
        top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
        right: 18,
        zIndex: 45,
        pointerEvents: 'none',
      }}
    >
      <BalancePill
        coinBalance={coinBalance}
        gemBalance={gemBalance}
        streak={streak}
        level={level}
        onTapGems={onTapGems}
        bonus={bonusProps}
      />
    </div>
  );

  // Social HUD — the broadcast reaction tray was removed (producer
  // 2026-06-08: "remove the general buttons, just keep the player
  // specific ones"). Directed one-to-one gestures now live solely on
  // the per-avatar SeatGestureButton; the to-the-table float-up
  // display stays per-phase (each phase view renders ReactionFloat
  // over its own seat avatars from the `reactions` prop).

  // Producer feedback 2026-05-27 — step-by-step debug mode. Render
  // a small "PAUSED · MANUAL MODE" pill in the top-left when the
  // server has manualMode = true, so a human producer (or Claude
  // watching the preview) can see at a glance that auto-progression
  // is frozen.
  const debugPausedBadge = liveState?.manualMode ? (
    <div
      // R3 capture mode hides this — manualMode is the right state
      // for predictable iPhone capture (bots paused), but the badge
      // pollutes the frame. See styles.css `.capture-mode` rule.
      data-capture-hide
      style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
        left: 14,
        // Above FirstEntryOverlay (z=90) so the badge stays visible
        // through every phase, including the "Setting the table"
        // loader and the match-wrap overlay.
        zIndex: 100,
        pointerEvents: 'none',
        background: 'rgba(255, 201, 64, 0.92)',
        color: '#1a0e2a',
        fontFamily: '"Fredoka", -apple-system, system-ui',
        fontWeight: 900,
        fontSize: 11,
        letterSpacing: 1,
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid rgba(0,0,0,0.25)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
      }}
    >
      ⏸ PAUSED · MANUAL MODE
    </div>
  ) : null;

  // U12 #2 (2026-05-30) — SSE reconnect badge. The wheel-room stream
  // can drop mid-match (network blip, server restart, browser
  // backgrounding). Without a visible cue the player sees a frozen
  // screen and assumes the game broke. This badge shows whenever the
  // stream isn't 'live' AND the player has a roomId — i.e. they're
  // actively in a match. EventSource auto-reconnects so most blips
  // resolve in ~1s; the badge is just there long enough to be
  // reassuring on a longer outage.
  //
  // Hidden on the loader (status: 'connecting' before any state has
  // landed) because the FirstEntryOverlay already covers that case
  // with its own "Setting the table" affordance. Once liveState is
  // populated, a drop back to 'connecting' / 'disconnected' / 'error'
  // means a mid-session disconnect — the real failure case this
  // surface is for.
  const showReconnectBadge =
    roomId !== null
    && liveState !== null
    && streamStatus !== 'live'
    && streamStatus !== 'idle';
  const reconnectBadge = showReconnectBadge ? (
    <div
      data-capture-hide
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        top: 'calc(env(safe-area-inset-top, 0px) + 4px)',
        // Sit below the PAUSED badge when both are visible (rare).
        // PAUSED is ~190px wide at typical text; 200 leaves a small
        // gap on the left edge. On a phone the PAUSED badge isn't
        // active outside dev, so this normally has the left corner
        // to itself.
        left: liveState?.manualMode ? 200 : 14,
        zIndex: 100,
        pointerEvents: 'none',
        background: 'rgba(232, 52, 78, 0.92)',
        color: '#fff',
        fontFamily: '"Fredoka", -apple-system, system-ui',
        fontWeight: 800,
        fontSize: 11,
        letterSpacing: 1,
        padding: '6px 12px',
        borderRadius: 999,
        border: '1px solid rgba(0,0,0,0.25)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.35)',
        textShadow: '0 1px 0 rgba(0,0,0,0.4)',
      }}
    >
      ⚠ RECONNECTING…
    </div>
  ) : null;

  // Audit backlog D3 (2026-05-28) — mobile dev panel. Mounts when
  // the URL search contains `?dev=panel`. Inert in production.
  const devPanel = isDevPanelEnabled()
    ? <WheelDevPanel state={liveState} roomId={roomId} />
    : null;

  // U11 (2026-05-30) — ProfileCard for the tapped cast member. Lives
  // at this chrome layer so it floats above every phase view's seat
  // strip. The card itself owns its z-index (200) + backdrop click
  // dismissal; we just need to render it when an archetype is
  // selected and wire the favourite-toggle handlers to the cast
  // hook. PLAYER taps don't reach this — the wheel-payout / reel-race
  // / resolve views all gate the tap on {@code seat.archetype !== null}.
  const profileCardOverlay = selectedArchetype !== null ? (
    <ProfileCard
      archetype={selectedArchetype}
      isFavorited={cast.favourites.has(selectedArchetype)}
      fanCount={cast.fanCounts[selectedArchetype] ?? 0}
      onFavoriteToggle={() => {
        void cast.toggle(selectedArchetype);
      }}
      onSendGesture={
        roomId && playerId
          ? (gestureId) => {
              // archetype → seat (each bot seat is a distinct archetype
              // in v1) → directed REACTION; the bot reciprocates a beat
              // later (server BotGestureInbox). Close on send.
              const target = liveState?.seats.find(
                (s) => s.kind === 'BOT' && s.archetype === selectedArchetype,
              );
              if (!target) return;
              // Remember we initiated, so their reply reads as "...back".
              rememberSentGesture(target.seatId, gestureId);
              void submitWheelCommand(roomId, 'REACTION', playerId, {
                reactionId: gestureId,
                targetSeatId: target.seatId,
              }).catch(() => {});
              setSelectedArchetype(null);
            }
          : undefined
      }
      onClose={() => setSelectedArchetype(null)}
    />
  ) : null;

  if (phaseContent) {
    return (
      <>
        {phaseContent}
        {walletChrome}
        {/* Directed gesture toast — "Bea high-fived you" + High five back.
            Top-center overlay above the avatar rail; renders null unless a
            one-to-one gesture is currently aimed at the local seat. */}
        {liveState ? (
          <GestureToast
            reactions={reactions}
            selfSeatId={playerSeatId}
            seats={liveState.seats}
            roomId={roomId}
            playerId={playerId}
          />
        ) : null}
        <SeatJoinToast toasts={seatToasts} />
        {debugPausedBadge}
        {reconnectBadge}
        {devPanel}
        {audienceChrome}
        {tutorialChrome}
        {profileCardOverlay}
      </>
    );
  }

  // The "preparation" surface — first entry, IDLE, and CHARGING all
  // render the same loader. Cast strip, inspect grid, phase pill,
  // SpinChargingButton, audience take-a-seat CTA — all gone. The
  // slot canvas (REEL_RACE) takes over the moment the room enters
  // its first playable phase.
  const backdropBiomeId = biomeIdForTheme(theme);
  const backdropUrl = BACKDROP_BY_BIOME[backdropBiomeId] ?? null;
  const loadingEyebrow = LOADING_EYEBROW_BY_BIOME[backdropBiomeId] ?? 'Setting the table';

  return (
    <div
      style={{
        position: 'relative',
        height: '100%',
        overflow: 'hidden',
        background:
          'radial-gradient(ellipse at top, #1B2C56 0%, #0B1838 60%, #060d22 100%)',
        color: '#E9F0FF',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      <FirstEntryOverlay
        key={prepCycleKey}
        backdropUrl={backdropUrl}
        eyebrow={loadingEyebrow}
        floorMs={FIRST_ENTRY_FLOOR_MS}
      />
      {errorMessage ? (
        <div
          role="alert"
          style={{
            position: 'absolute',
            top: 12,
            left: 12,
            right: 12,
            zIndex: 91,
            padding: '8px 12px',
            background: 'rgba(232, 52, 78, 0.85)',
            border: '1px solid rgba(255, 200, 200, 0.65)',
            borderRadius: 10,
            fontSize: 11,
            color: '#FFF',
            textAlign: 'center',
          }}
        >
          ⚠ Couldn't reach the room. {errorMessage}
        </div>
      ) : null}
      {walletChrome}
      {debugPausedBadge}
      {reconnectBadge}
      {devPanel}
      {tutorialChrome}
      {profileCardOverlay}
    </div>
  );
}

/** First-entry loading overlay. Mounts on roomId set, fills a bar
 *  0→100% over {@code floorMs}, then is unmounted by the parent
 *  once both the SSE state has landed and the floor has elapsed.
 *
 *  Chrome mirrors the WheelNodePreview story card:
 *  gold double-border, ribbon-style eyebrow, scattered sparkles,
 *  corner sparks, radial-gradient interior. The instructional
 *  copy ("Collect the gold coins for the next round.") preps the
 *  player for the REEL_RACE phase BEFORE it starts so the spin
 *  window isn't blocked by a tutorial overlay. */
function FirstEntryOverlay({
  backdropUrl,
  eyebrow,
  floorMs,
}: {
  backdropUrl: string | null;
  eyebrow: string;
  floorMs: number;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 90,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background:
          'radial-gradient(ellipse at top, #1B2C56 0%, #0B1838 60%, #060d22 100%)',
        animation: 'gtFadeIn 0.2s ease-out',
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      {backdropUrl ? (
        <img
          src={backdropUrl}
          alt=""
          aria-hidden
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            opacity: 0.45,
            pointerEvents: 'none',
          }}
        />
      ) : null}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at center, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.7) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* Story-card chrome — same gold-bordered radial gradient panel
          + ribbon eyebrow + corner sparks of the story-card dialogs. */}
      <div
        style={{
          position: 'relative',
          width: 'min(340px, 84vw)',
          marginTop: 24,
          animation: 'gtPop 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {/* Ribbon eyebrow — biome-flavoured short verb. */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'translateY(-50%)',
          }}
        >
          <div
            style={{
              position: 'relative',
              borderRadius: 999,
              border: '3px solid rgba(255,201,64,0.95)',
              background:
                'linear-gradient(180deg, #6B3D77 0%, #3B1F50 100%)',
              padding: '7px 32px',
              boxShadow:
                '0 4px 10px rgba(0,0,0,0.5), inset 0 -2px 3px rgba(0,0,0,0.35), inset 0 2px 3px rgba(255,255,255,0.4)',
              whiteSpace: 'nowrap',
            }}
          >
            <PearlStud side="left" />
            <PearlStud side="right" />
            <h2
              style={{
                margin: 0,
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: 1,
                color: '#fff',
                textShadow: '0 2px 0 rgba(60,20,90,0.65)',
                fontFamily: 'inherit',
                textTransform: 'uppercase',
              }}
            >
              {eyebrow}
            </h2>
          </div>
        </div>

        <div
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 24,
            border: '3px solid rgba(255,201,64,0.85)',
            paddingTop: 32,
            paddingBottom: 22,
            paddingLeft: 24,
            paddingRight: 24,
            color: '#E9F0FF',
            background:
              'radial-gradient(ellipse at 50% 0%, #2A1352 0%, #1a0830 65%, #100520 100%)',
            boxShadow:
              '0 30px 60px rgba(0,0,0,0.55), inset 0 0 0 2px rgba(255,210,90,0.15)',
            textAlign: 'center',
          }}
        >
          <CornerSpark position="top-left" />
          <CornerSpark position="top-right" />
          <CornerSpark position="bottom-left" />
          <CornerSpark position="bottom-right" />
          <Sparkle top="22%" left="8%" size={6} rotation={15} />
          <Sparkle top="46%" left="92%" size={8} rotation={-22} />
          <Sparkle top="68%" left="6%" size={5} rotation={40} />
          <Sparkle top="32%" left="86%" size={5} rotation={5} />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              margin: '0 auto 12px',
            }}
          >
            <img
              src={bonusCoinUrl}
              alt=""
              aria-hidden
              draggable={false}
              style={{
                display: 'block',
                width: 72,
                height: 72,
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.55))',
              }}
            />
            <img
              src={itemDropGiftUrl}
              alt=""
              aria-hidden
              draggable={false}
              style={{
                display: 'block',
                width: 72,
                height: 72,
                objectFit: 'contain',
                filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.55))',
              }}
            />
          </div>
          <div
            style={{
              fontSize: 17,
              fontWeight: 800,
              lineHeight: 1.3,
              marginBottom: 16,
              textShadow: '0 2px 4px rgba(0,0,0,0.55)',
            }}
          >
            Collect gold coins and special items for the next round.
          </div>
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: 8,
              borderRadius: 999,
              background: 'rgba(255,255,255,0.16)',
              boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.4)',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                borderRadius: 999,
                background:
                  'linear-gradient(90deg, #FFD96B 0%, #FFC940 60%, #F4B860 100%)',
                boxShadow: '0 0 12px rgba(255,201,64,0.55)',
                transformOrigin: '0% 50%',
                animation: `gtLoadBar ${floorMs}ms cubic-bezier(0.4, 0.0, 0.2, 1) forwards`,
              }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gtLoadBar {
          0%   { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
      `}</style>
    </div>
  );
}

/** Shared shell for one-shot per-session tutorial overlays. Each
 *  caller passes its own copy + persistence key; this component owns
 *  the modal chrome, the localStorage round-trip, and the dismiss
 *  semantics so the per-session wrappers stay short. */
function TutorialOverlayCard({
  storageKey,
  eyebrow,
  headline,
  body,
  cta = 'Got it',
}: {
  storageKey: string;
  eyebrow: string;
  headline: string;
  body: string;
  cta?: string;
}) {
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(storageKey) !== '1';
    } catch {
      return true;
    }
  });
  if (!open) return null;
  const dismiss = () => {
    try { window.localStorage.setItem(storageKey, '1'); }
    catch { /* private mode / quota — fall through, hide locally */ }
    setOpen(false);
  };
  return (
    <div
      onClick={dismiss}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 80,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
        fontFamily: '"Fredoka", -apple-system, system-ui',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          maxWidth: 340,
          background: 'linear-gradient(180deg, #1B2C56 0%, #0B1838 100%)',
          color: '#E9F0FF',
          borderRadius: 22,
          padding: '24px 22px 20px',
          textAlign: 'center',
          border: '2.5px solid rgba(255,201,64,0.55)',
          boxShadow: '0 25px 50px rgba(0,0,0,0.55)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 1.4,
            textTransform: 'uppercase',
            color: '#FFC940',
            marginBottom: 6,
          }}
        >
          {eyebrow}
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', lineHeight: 1.2 }}>
          {headline}
        </div>
        <div
          style={{
            fontSize: 14,
            color: '#C7D3F0',
            marginTop: 10,
            lineHeight: 1.45,
            whiteSpace: 'pre-line',
          }}
        >
          {body}
        </div>
        <button
          type="button"
          onClick={dismiss}
          style={{
            marginTop: 18,
            width: '100%',
            height: 44,
            border: 'none',
            borderRadius: 14,
            background: 'linear-gradient(180deg, #FFD96B 0%, #FFC940 100%)',
            color: '#3A2700',
            fontSize: 14,
            fontWeight: 800,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            cursor: 'pointer',
            boxShadow: '0 4px 0 #B58A00, 0 8px 16px rgba(0,0,0,0.35)',
            fontFamily: 'inherit',
          }}
        >
          {cta}
        </button>
      </div>
    </div>
  );
}

/** Session 2 Stake-screen tutorial — UX §4.2 microcopy. Fires the
 *  first time the player lands in the STAKE phase on session 2, when
 *  Shield + Lance are still locked and Reach is the only interactive
 *  pot. */
function Session2StakeOverlay() {
  return (
    <TutorialOverlayCard
      storageKey="gtc.tutorial.wheel.session2.stake.dismissed"
      eyebrow="Stake"
      headline="Earn tokens. They decide your share of the bonus."
      body={
        "Drop your tokens into Reach to grow your slice on the wheel. " +
        "Shield and Lance unlock once you've found your feet."
      }
    />
  );
}

/** Session 4 jackpot-unlocked celebration — UX §4.4. Fires the first
 *  time the server-side onboarding pointer hits 4, just after the
 *  player crosses the 50-token jackpot-eligibility threshold for the
 *  first time. */
function Session4JackpotUnlockedOverlay() {
  return (
    <TutorialOverlayCard
      storageKey="gtc.tutorial.wheel.session4.jackpot.dismissed"
      eyebrow="Unlocked"
      headline="🎰 Jackpot unlocked"
      body={
        "Earn 50+ tokens in a Reel Race and you're in for the jackpot " +
        "spin too. The wheel's third sub-spin, biggest payout in the room."
      }
      cta="Let's go"
    />
  );
}

/** Floating bottom-of-screen CTA for audience-mode spectators —
 *  prompts the user to promote into a seat. The button is disabled
 *  with a "Wait for a seat" affordance when the room is full (all
 *  PLAYER seats online; no bots to bump).
 *
 *  <p>F1.next watch-first gate: when {@code watchFirstUntilMs} is in
 *  the future, the button stays disabled with a ticking countdown
 *  (UX §4.1 — "Skip available after 30s"). Player can't bypass; the
 *  curriculum forces 30s of observation before promoting to a seat.
 */
function AudienceModeCta({
  onTakeSeat,
  seatsAvailable,
  watchFirstUntilMs,
}: {
  onTakeSeat: (() => void) | null;
  seatsAvailable: boolean;
  watchFirstUntilMs?: number | null;
}) {
  // Tick to drive the countdown re-render. 250ms is fine — the
  // displayed value rounds to whole seconds anyway.
  const [now, setNow] = useState<number>(() => Date.now());
  const watchFirstActive =
    watchFirstUntilMs !== null && watchFirstUntilMs !== undefined && watchFirstUntilMs > now;
  useEffect(() => {
    if (!watchFirstActive) return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => window.clearInterval(id);
  }, [watchFirstActive]);

  const watchFirstRemainingSec = watchFirstActive
    ? Math.max(0, Math.ceil((watchFirstUntilMs! - now) / 1000))
    : 0;
  const disabled = !onTakeSeat || !seatsAvailable || watchFirstActive;

  let buttonLabel: string;
  if (watchFirstActive) {
    buttonLabel = `Watching… 0:${watchFirstRemainingSec.toString().padStart(2, '0')}`;
  } else if (!seatsAvailable) {
    buttonLabel = 'Room full · keep watching';
  } else {
    buttonLabel = 'Take a seat ▶';
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 50,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          background: 'rgba(11,24,56,0.85)',
          backdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,201,64,0.5)',
          borderRadius: 999,
          padding: '4px 10px',
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: 1.2,
          textTransform: 'uppercase',
          color: '#FFC940',
        }}
      >
        {watchFirstActive ? '◉ Tutorial mode' : '◉ Watching'}
      </div>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) onTakeSeat?.();
        }}
        style={{
          pointerEvents: 'auto',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          height: 44,
          padding: '0 22px',
          border: 'none',
          borderRadius: 14,
          background: disabled
            ? 'rgba(255,255,255,0.12)'
            : 'linear-gradient(180deg, #FFD96B 0%, #FFC940 100%)',
          color: disabled ? '#9aa6c0' : '#3A2700',
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 0.5,
          textTransform: 'uppercase',
          boxShadow: disabled
            ? 'none'
            : '0 4px 0 #B58A00, 0 8px 16px rgba(0,0,0,0.3)',
          fontFamily: 'inherit',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {buttonLabel}
      </button>
    </div>
  );
}

/**
 * Build a SpinSlotResponse from the server-side reel grid + settlement.
 *
 * The grid arrives row-major (15 cells, 5 cols × 3 rows); the slot
 * scene expects `grid[col][row]`, so we transpose. The settlement is
 * computed server-side by SettleSpinService against the same weighted
 * symbol roster the standalone slot uses — bonus cells overlay the
 * grid as non-paying items, so `settlements` here is real payline
 * math (3+ matches on a payline). The slot scene's `highlightWins`
 * fires on it the same way it would for a standalone slot spin, so
 * the player gets the visual celebration when their grid actually
 * has a payline win.
 *
 * <p>Wallet handling: Reel Race spins fund from the meter pool
 * (no per-spin debit on the server), so we mint a synthetic
 * post-spin wallet that nets bet vs payline winnings. The slot HUD
 * is hidden during Reel Race (`hideHudBalance: true` upstream), so
 * the synthetic value isn't shown — but the slot scene's local
 * wallet model still mirrors it for the take-win animation.
 */
function buildSpinResponse(
  rowMajorGrid: readonly string[],
  _seatTokens: number,
  bet: number,
  walletCoins: number,
  settlement: WheelSpinSettlement | null,
): SpinSlotResponse {
  const cols = 5;
  const rows = 3;
  const grid: string[][] = [];
  for (let c = 0; c < cols; c++) {
    const reel: string[] = [];
    for (let r = 0; r < rows; r++) {
      reel.push(rowMajorGrid[r * cols + c] ?? 'cherry');
    }
    grid.push(reel);
  }
  // Map the server's settlement to the slot scene's expected shape.
  // The server's notional bet is 1 (REEL_RACE_NOTIONAL_BET in
  // WheelGameRules); echo that bet through so the slot scene's
  // describeWin / take-win math reads consistently.
  const totalWinnings = settlement?.totalWinnings ?? 0;
  const sceneSettlement: SpinSlotResponse['settlement'] = settlement
    ? {
        totalWinnings: settlement.totalWinnings,
        bet: settlement.bet,
        net: settlement.net,
        settlements: settlement.settlements.map((s) => ({
          lineId: s.lineId,
          matchSymbolId: s.matchSymbolId,
          matchCount: s.matchCount,
          hadWild: s.hadWild,
          winnings: s.winnings,
        })),
        scatter: settlement.scatter
          ? {
              scatterCount: settlement.scatter.scatterCount,
              matchSymbolId: settlement.scatter.matchSymbolId,
              winnings: settlement.scatter.winnings,
              positions: settlement.scatter.positions.map((p) => ({
                col: p.col,
                row: p.row,
              })),
            }
          : null,
      }
    : { totalWinnings: 0, bet, net: -bet, settlements: [], scatter: null };
  return {
    spinId: `reel-race-${Date.now()}`,
    outcome: { grid },
    settlement: sceneSettlement,
    // The slot scene only reads wallet.coins; the brand-typed id /
    // playerId fields are for the legacy wallet API. We mint synthetic
    // placeholder values here — the multiplayer flow doesn't surface
    // the wallet object anywhere else after the slot scene mirrors
    // its coins onto the local model. Coins land at (balance − bet +
    // winnings) so the slot scene's setBalance/runCollect math
    // (post-debit, pre-credit, then take-win to wallet.coins) works
    // out to (balance − bet + winnings).
    wallet: {
      id: 'reel-race-wallet' as unknown as SpinSlotResponse['wallet']['id'],
      playerId: 'reel-race-player' as unknown as SpinSlotResponse['wallet']['playerId'],
      coins: Math.max(0, walletCoins - bet + totalWinnings),
      gems: 0,
      created: new Date().toISOString(),
    },
    wasFreeSpin: false,
    freeSpinsGranted: 0,
    freeSpinsRemaining: 0,
    holdAndSpin: null,
  };
}

// ---- helpers ---------------------------------------------------

// (Removed in the loader-replaces-lobby cleanup: PHASE_PLAYER_COPY,
// loadFavourites/saveFavourites, sample-seat helpers, projectSeat,
// computeWatchingCount, SpinChargingButton, mock stat helpers — all
// fed the deleted cast-strip / inspect-grid lobby surface.)
